export type TalkBrandId = "ctf" | "triadix" | "chess";

export const TALK_ORIGINS = [
  "https://talk.ctfonly.fans",
  "https://talk.3dx.sh",
  "https://onlychess.nationalrussianchess.ru",
] as const;

const CTF_ROSTER_LABELS = [
  "Azure War-Fan Assassin",
  "Violet Shadow Dancer",
  "Obsidian Blood-Mage",
  "Rose Thorn Assassin",
  "Crimson Chainblade Duelist",
  "Ivory Frost Huntress",
  "Jade Serpent Adept",
  "Golden Sun Champion",
  "Silver Moonblade Dancer",
  "Copper Arena Brawler",
  "Cobalt Storm Monk",
  "Inferno Twinblade Rebel",
] as const;

const CHESS_ROSTER_LABELS = [
  "Белый король",
  "Белая ферзь",
  "Белая ладья",
  "Белый слон",
  "Белый конь",
  "Белая пешка",
  "Чёрный король",
  "Чёрная ферзь",
  "Чёрная ладья",
  "Чёрный слон",
  "Чёрный конь",
  "Чёрная пешка",
] as const;

const TRIADIX_ROSTER_LABELS = [
  "North",
  "Relay",
  "Vector",
  "Summit",
  "Forge",
  "Signal",
  "Harbor",
  "Atlas",
  "Pillar",
  "Orbit",
  "Beacon",
  "Vertex",
] as const;

export interface TalkBrandConfig {
  id: TalkBrandId;
  productName: string;
  profileLabel: string;
  organizationName: string;
  markLabel: string;
  heroLines: readonly [string, string, string];
  heroDescription: string;
  heroAssurance: string;
  previewLabel: string;
  previewRoomName: string;
  neutralAvatars: boolean;
  supportsHaramMode: boolean;
  origin: string;
  storagePrefix: string;
  guestStoragePrefix: string;
  rosterLabels: readonly string[];
}

function requestedBrand(): TalkBrandId | undefined {
  const value = import.meta.env.VITE_TALK_BRAND?.trim().toLowerCase();
  if (value === "triadix" || value === "chess" || value === "ctf") return value;
  return undefined;
}

export function originForHost(hostname: string): string {
  const host = hostname.toLowerCase();
  if (host === "talk.3dx.sh") return "https://talk.3dx.sh";
  if (host === "onlychess.nationalrussianchess.ru") return "https://onlychess.nationalrussianchess.ru";
  return "https://talk.ctfonly.fans";
}

export function brandIdForHost(hostname: string, explicit?: TalkBrandId): TalkBrandId {
  if (explicit) return explicit;
  const host = hostname.toLowerCase();
  if (host === "talk.3dx.sh") return "triadix";
  if (host === "onlychess.nationalrussianchess.ru") return "chess";
  return "ctf";
}

const runtimeHostname = typeof window === "undefined" ? "talk.ctfonly.fans" : window.location.hostname;
const runtimeOrigin = typeof window === "undefined" || !window.location.origin
  ? originForHost(runtimeHostname)
  : window.location.origin;
const brandId = brandIdForHost(runtimeHostname, requestedBrand());

const ctfBrand: TalkBrandConfig = {
  id: "ctf",
  productName: "CTFOnly Talk",
  profileLabel: "Talk",
  organizationName: "CTFOnly",
  markLabel: "C",
  heroLines: ["Talk.", "Share.", "Solve."],
  heroDescription: "Private browser rooms for clear conversation and sharp, readable screen sharing. No install. Guests join from a link.",
  heroAssurance: "Private 1:1 calls use verified end-to-end encryption and prefer direct media.",
  previewLabel: "Illustrative preview",
  previewRoomName: "Briefing room",
  neutralAvatars: false,
  supportsHaramMode: true,
  origin: runtimeOrigin === "https://talk.3dx.sh" ? runtimeOrigin : "https://talk.ctfonly.fans",
  storagePrefix: "ctfonlyfans",
  guestStoragePrefix: "ctfonly-talk",
  rosterLabels: CTF_ROSTER_LABELS,
};

const triadixBrand: TalkBrandConfig = {
  id: "triadix",
  productName: "Triadix Talk",
  profileLabel: "Triadix Talk",
  organizationName: "Triadix",
  markLabel: "T",
  heroLines: ["Talk.", "Align.", "Deliver."],
  heroDescription: "Focused browser rooms for clear decisions, dependable screen sharing, and work that keeps moving. No installation required.",
  heroAssurance: "Private 1:1 calls use verified end-to-end encryption and prefer direct media.",
  previewLabel: "Room preview",
  previewRoomName: "Operations room",
  neutralAvatars: true,
  supportsHaramMode: false,
  origin: runtimeOrigin,
  storagePrefix: "triadix",
  guestStoragePrefix: "triadix-talk",
  rosterLabels: TRIADIX_ROSTER_LABELS,
};

const chessBrand: TalkBrandConfig = {
  id: "chess",
  productName: "OnlyChess",
  profileLabel: "OnlyChess",
  organizationName: "OnlyChess",
  markLabel: "♘",
  heroLines: ["Говорите.", "Смотрите.", "Играйте."],
  heroDescription: "Голос, камера и экран прямо в игровой комнате. Без установки: соперник заходит по ссылке.",
  heroAssurance: "Камера выключена — вместо лица шахматная фигура. Живое видео её не подменяет.",
  previewLabel: "Игровая комната",
  previewRoomName: "Партия",
  neutralAvatars: true,
  supportsHaramMode: false,
  origin: typeof window === "undefined" ? "https://onlychess.nationalrussianchess.ru" : runtimeOrigin,
  storagePrefix: "onlychess",
  guestStoragePrefix: "onlychess-talk",
  rosterLabels: CHESS_ROSTER_LABELS,
};

export const talkBrand: TalkBrandConfig = brandId === "triadix" ? triadixBrand : brandId === "chess" ? chessBrand : ctfBrand;

export function isTriadixBrand(): boolean {
  return talkBrand.id === "triadix";
}

export function isAllowedTalkOrigin(origin: string): boolean {
  return (TALK_ORIGINS as readonly string[]).includes(origin);
}

export function applyTalkBrandDocument(config = talkBrand): void {
  if (typeof document === "undefined") return;
  document.title = config.productName;
  document.documentElement.lang = config.id === "chess" ? "ru" : "en";
  document.documentElement.dataset.talkBrand = config.id;
  const description = document.querySelector<HTMLMetaElement>('meta[name="description"]');
  if (description) description.content = config.id === "chess"
    ? "OnlyChess — голос, камера и экран в игровой комнате."
    : `${config.productName} — focused browser rooms for conversation and screen sharing.`;
}
